/**
 * What a Brain role can be granted, area by area, in sidebar order. The same
 * model as the Control Center's administrator roles: per area none, read,
 * custom (read plus named actions) or write (read plus every action). A
 * person holds one role (shared/one-role.ts), and their access is that
 * role's grants.
 *
 * Shared by the Worker (enforcement) and the console (editor, disabled
 * actions, locked pages).
 */
import { BANK_ACTIVATE, QUOTE_AREAS, applyQuoteGrants } from "./areas-quotes";

export type Level = "none" | "read" | "custom" | "write";
export type Grant = { level: Level; actions?: string[] };
export type Grants = Record<string, Grant>;

export type Area = {
  key: string;
  section: string;
  label: string;
  description: string;
  icon: string;
  levels: Level[];
  actions: { key: string; label: string }[];
};

export const SECTIONS = ["Customers", "Support", "Releases", "Outreach", "Platform", "Business", "Administration"] as const;

export const AREAS: Area[] = [
  {
    key: "tenants", section: "Customers", label: "Tenants", icon: "building-multiple",
    description: "Every tenant, its licence, infrastructure and policy.",
    levels: ["none", "read", "custom", "write"],
    actions: [
      { key: "create", label: "Provision tenants" },
      { key: "licence", label: "Change licence and modules" },
      { key: "infrastructure", label: "Resize and verify" },
      { key: "updates", label: "Change rings and windows" },
      { key: "policy", label: "Lock tenant policy" },
      { key: "allowlist", label: "Change the API allowlist" },
      { key: "suspend", label: "Suspend and resume" },
      { key: "delete", label: "Delete tenants" },
    ],
  },
  {
    key: "break_glass", section: "Customers", label: "Break-glass access", icon: "shield-keyhole",
    description: "Enter a tenant's Control Center under audit.",
    levels: ["none", "read", "custom", "write"],
    actions: [
      { key: "read_only", label: "Open read only" },
      { key: "read_write", label: "Open with write access" },
    ],
  },
  {
    key: "partners", section: "Customers", label: "Partners", icon: "handshake",
    description: "Partner accounts and their Partner Portal access.",
    levels: ["none", "read", "custom", "write"],
    actions: [
      { key: "onboard", label: "Onboard partners" },
      { key: "update", label: "Change tier and terms" },
      { key: "suspend", label: "Suspend partners" },
    ],
  },
  {
    key: "provisioning", section: "Customers", label: "Provisioning jobs", icon: "arrow-sync-circle",
    description: "Infrastructure jobs and their logs.",
    levels: ["none", "read", "custom", "write"],
    actions: [
      { key: "resume", label: "Resume failed jobs" },
      { key: "cancel", label: "Cancel jobs" },
    ],
  },
  {
    key: "tickets", section: "Support", label: "Tickets", icon: "ticket-diagonal",
    description: "Support requests from tenants and partners.",
    levels: ["none", "read", "custom", "write"],
    actions: [
      { key: "reply", label: "Reply to requesters" },
      { key: "assign", label: "Assign and prioritise" },
      { key: "solve", label: "Solve and reopen" },
      { key: "expansion", label: "Decide PoP expansion requests" },
    ],
  },
  {
    key: "feedback", section: "Support", label: "Feedback", icon: "person-feedback",
    description: "Ratings and comments from the Control Center.",
    levels: ["none", "read", "custom", "write"],
    actions: [{ key: "reply", label: "Reply and open tickets" }],
  },
  {
    key: "contacts", section: "Support", label: "Customer contact details", icon: "contact-card",
    description: "Phone numbers and emails on tickets and owners.",
    levels: ["none", "read"],
    actions: [],
  },
  {
    key: "platform_updates", section: "Releases", label: "Platform updates", icon: "rocket",
    description: "Roll platform releases out to tenants.",
    levels: ["none", "read", "custom", "write"],
    actions: [
      { key: "push", label: "Start a rollout" },
      { key: "pause", label: "Pause and resume" },
      { key: "rollback", label: "Roll back tenants" },
    ],
  },
  {
    key: "windows", section: "Releases", label: "Maintenance windows", icon: "calendar-clock",
    description: "When upgrades may run, and change freezes.",
    levels: ["none", "read", "write"],
    actions: [],
  },
  {
    key: "agent_updates", section: "Releases", label: "Agent updates", icon: "arrow-download",
    description: "Agent versions, auto-update and package sets.",
    levels: ["none", "read", "custom", "write"],
    actions: [
      { key: "rebuild", label: "Rebuild package sets" },
      { key: "channels", label: "Move the channels" },
    ],
  },
  {
    key: "announcements", section: "Outreach", label: "Announcements", icon: "megaphone",
    description: "Banners in the Control Center, Workspace and Partner Portal.",
    levels: ["none", "read", "write"],
    actions: [],
  },
  {
    key: "email", section: "Outreach", label: "Email & notifications", icon: "mail",
    description: "Delivery, templates and notification rules.",
    levels: ["none", "read", "write"],
    actions: [],
  },
  {
    key: "docs", section: "Outreach", label: "Documentation", icon: "book-open",
    description: "Who may read the product docs, and which pages are public or hidden.",
    levels: ["none", "read", "custom", "write"],
    actions: [
      { key: "requests", label: "Decide access requests" },
      { key: "readers", label: "Add and remove readers, end sessions" },
      { key: "publishing", label: "Publish and hide pages, set the access time" },
    ],
  },
  {
    key: "service_status", section: "Platform", label: "Service status", icon: "pulse",
    description: "Service health, incidents and maintenance.",
    levels: ["none", "read", "custom", "write"],
    actions: [
      { key: "incident", label: "Post incidents and updates" },
      { key: "override", label: "Override a tenant's status" },
    ],
  },
  {
    key: "fleet", section: "Platform", label: "Hosts & operations", icon: "server",
    description: "Tenant hosts, their agents and operations on services.",
    levels: ["none", "read", "custom", "write"],
    actions: [
      { key: "restart", label: "Restart services" },
      { key: "diagnostics", label: "Collect diagnostics" },
      { key: "enroll", label: "Enrol and retire hosts" },
    ],
  },
  {
    key: "clouds", section: "Platform", label: "Clouds & regions", icon: "cloud",
    description: "Cloud accounts, manual providers, regions and PoPs.",
    levels: ["none", "read", "write"],
    actions: [],
  },
  {
    key: "revenue", section: "Business", label: "Revenue and pricing", icon: "money",
    description: "Recurring revenue, price per seat and partner discounts.",
    levels: ["none", "read", "write"],
    actions: [],
  },
  ...QUOTE_AREAS,
  {
    key: "team", section: "Administration", label: "Team", icon: "people-team",
    description: "Inlinea staff who can use the Brain.",
    levels: ["none", "read", "write"],
    actions: [],
  },
  {
    key: "roles", section: "Administration", label: "Roles & permissions", icon: "shield-person",
    description: "What each role can see and change.",
    levels: ["none", "read", "write"],
    actions: [],
  },
  {
    key: "audit", section: "Administration", label: "Audit trail", icon: "history",
    description: "Every action taken in the Brain.",
    levels: ["none", "read"],
    actions: [],
  },
];

export const AREA_BY_KEY = Object.fromEntries(AREAS.map((a) => [a.key, a])) as Record<string, Area>;

const RANK: Record<Level, number> = { none: 0, read: 1, custom: 2, write: 3 };

/** Merges grants: per area the higher level wins; two custom grants allow the union of actions. */
export function mergeGrants(list: Grants[]): Grants {
  const out: Grants = {};
  for (const grants of list) {
    for (const [key, g] of Object.entries(grants)) {
      const cur = out[key];
      if (!cur || RANK[g.level] > RANK[cur.level]) {
        out[key] = { level: g.level, actions: g.level === "custom" ? [...(g.actions ?? [])] : undefined };
      } else if (g.level === "custom" && cur.level === "custom") {
        cur.actions = [...new Set([...(cur.actions ?? []), ...(g.actions ?? [])])];
      }
    }
  }
  return out;
}

export function canRead(grants: Grants, area: string): boolean {
  return RANK[grants[area]?.level ?? "none"] >= RANK.read;
}

/** Write, or custom with the action. Without an action, any write-capable grant. */
export function canDo(grants: Grants, area: string, action?: string): boolean {
  const g = grants[area];
  if (!g) return false;
  if (g.level === "write") return true;
  if (g.level === "custom") return action ? (g.actions ?? []).includes(action) : (g.actions ?? []).length > 0;
  return false;
}

/**
 * The areas where `wanted` reaches beyond `mine`: a higher level, or a custom
 * action `mine` does not hold. Empty when `mine` covers every grant in `wanted`.
 */
export function beyond(mine: Grants, wanted: Grants): string[] {
  const out: string[] = [];
  for (const [key, g] of Object.entries(wanted)) {
    if (!g || g.level === "none") continue;
    const m = mine[key];
    const have = RANK[m?.level ?? "none"];
    if (have === RANK.write) continue;
    if (RANK[g.level] > have) out.push(key);
    else if (g.level === "custom" && (g.actions ?? []).some((a) => !(m?.actions ?? []).includes(a))) out.push(key);
  }
  return out;
}

/**
 * Actions so destructive that any custom role able to take them must also
 * carry their safeguards: a security key and a second approver. Who that
 * approver is comes from shared/approvals.ts: deleting a tenant always waits
 * for an Owner, whatever the role says.
 */
export const GUARDED_ACTIONS = [
  { approver: "tenants.delete", area: "tenants", action: "delete" },
  { approver: "break_glass.read_write", area: "break_glass", action: "read_write" },
] as const;

/** The second-approver steps a role's grants force on it. */
export function forcedApprovals(grants: Grants): string[] {
  return GUARDED_ACTIONS.filter((x) => canDo(grants, x.area, x.action)).map((x) => x.approver);
}

export function summarise(grants: Grants): Record<Level, number> {
  const out: Record<Level, number> = { none: 0, read: 0, custom: 0, write: 0 };
  for (const a of AREAS) out[grants[a.key]?.level ?? "none"]++;
  return out;
}

export type Safeguards = {
  require_key?: boolean;
  allowed_networks?: string[];
  second_approver?: string[]; // action ids: SECOND_APPROVER_ACTIONS
  session_hours?: number;
};

export const SECOND_APPROVER_ACTIONS = [
  { key: "tenants.delete", label: "Delete tenants" },
  { key: "break_glass.read_write", label: "Break-glass with write" },
  // The last editor of a bank account can't activate it; a second member with Bank details write, or an Owner, does (routes/quotes/bank.ts).
  { key: BANK_ACTIVATE, label: "Activating a bank account" },
] as const;

const all = (level: Level): Grants => Object.fromEntries(AREAS.map((a) => [a.key, { level: a.levels.includes(level) ? level : a.levels.includes("read") ? "read" : "none" }]));

export type BuiltinRole = {
  id: string;
  name: string;
  description: string;
  grants: Grants;
  safeguards: Safeguards;
  /** Second-approver steps this built-in role is trusted to take alone; its grants do not force them on it. */
  selfApproves?: string[];
};

/**
 * Built-in roles, highest first. Read only in the console; duplicate one to
 * make a custom role (a copy never carries `selfApproves`, so it waits for
 * approvers like any custom role).
 */
export const BUILTIN_ROLES: BuiltinRole[] = [
  {
    id: "builtin:owner",
    name: "Owner",
    description: "Everything, and the final approval to delete or suspend a tenant or to override a partner's discount.",
    grants: all("write"),
    safeguards: { require_key: true, session_hours: 8 },
    selfApproves: ["tenants.delete", "break_glass.read_write", BANK_ACTIVATE],
  },
  {
    id: "builtin:super_admin",
    name: "Super admin",
    description: "Everything, including team and roles. Deleting or suspending a tenant and overriding a partner's discount wait for an Owner.",
    grants: all("write"),
    safeguards: { require_key: true, second_approver: ["tenants.delete"], session_hours: 8 },
    selfApproves: ["break_glass.read_write"],
  },
  {
    id: "builtin:platform_engineer",
    name: "Platform engineer",
    description: "Provisioning, infrastructure, hosts and service status.",
    grants: {
      ...all("read"),
      tenants: { level: "custom", actions: ["create", "infrastructure", "updates", "allowlist", "suspend"] },
      break_glass: { level: "write" },
      provisioning: { level: "write" },
      fleet: { level: "write" },
      clouds: { level: "write" },
      service_status: { level: "write" },
      revenue: { level: "none" },
      team: { level: "read" },
      roles: { level: "read" },
    },
    safeguards: { require_key: true, second_approver: ["break_glass.read_write"], session_hours: 10 },
  },
  {
    id: "builtin:release_manager",
    name: "Release manager",
    description: "Plans and runs platform and agent releases.",
    grants: {
      ...all("read"),
      tenants: { level: "custom", actions: ["updates"] },
      platform_updates: { level: "write" },
      windows: { level: "write" },
      agent_updates: { level: "write" },
      fleet: { level: "custom", actions: ["restart"] },
      revenue: { level: "none" },
    },
    safeguards: { require_key: true, session_hours: 10 },
  },
  {
    id: "builtin:support_lead",
    name: "Support lead",
    description: "Runs the queue; can change priorities and open Control Centers.",
    grants: {
      ...all("read"),
      tickets: { level: "write" },
      feedback: { level: "write" },
      break_glass: { level: "write" },
      service_status: { level: "custom", actions: ["incident", "override"] },
      announcements: { level: "write" },
      docs: { level: "write" },
      revenue: { level: "none" },
      roles: { level: "none" },
    },
    safeguards: { require_key: true, session_hours: 10 },
  },
  {
    id: "builtin:support_engineer",
    name: "Support engineer",
    description: "Answers tickets and investigates read only.",
    grants: {
      tenants: { level: "read" },
      partners: { level: "read" },
      provisioning: { level: "read" },
      tickets: { level: "custom", actions: ["reply", "assign"] },
      feedback: { level: "read" },
      contacts: { level: "read" },
      break_glass: { level: "custom", actions: ["read_only"] },
      platform_updates: { level: "read" },
      windows: { level: "read" },
      agent_updates: { level: "read" },
      service_status: { level: "read" },
      fleet: { level: "read" },
      clouds: { level: "read" },
      docs: { level: "read" },
    },
    safeguards: { session_hours: 10 },
  },
  {
    id: "builtin:finance",
    name: "Finance",
    description: "Revenue, pricing and discounts.",
    grants: {
      tenants: { level: "custom", actions: ["licence"] },
      partners: { level: "custom", actions: ["update"] },
      revenue: { level: "write" },
      contacts: { level: "read" },
    },
    safeguards: { session_hours: 10 },
  },
  {
    id: "builtin:security_auditor",
    name: "Security auditor",
    description: "Reads everything. Changes nothing.",
    grants: all("read"),
    safeguards: { session_hours: 8 },
  },
];

// Quotes & Costing: the six quote areas per built-in role, and the Account Manager role (shared/areas-quotes.ts).
applyQuoteGrants(BUILTIN_ROLES);
